import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { ImagePerformanceMonitor, reportImagePerformance } from './ImagePerformanceMonitor';

// Image variant configuration
const VARIANTS = {
  'thumbnails/64x64': { width: 64, height: 64, maxSize: 50 * 1024 }, // 50KB
  'thumbnails/150x150': { width: 150, height: 150, maxSize: 75 * 1024 }, // 75KB
  'thumbnails/300x225': { width: 300, height: 225, maxSize: 100 * 1024 }, // 100KB
  'hero/400x300': { width: 400, height: 300, maxSize: 200 * 1024 }, // 200KB
  'hero/800x600': { width: 800, height: 600, maxSize: 500 * 1024 }, // 500KB
  'hero/1200x900': { width: 1200, height: 900, maxSize: 800 * 1024 }, // 800KB
  'full/1920x1440': { width: 1920, height: 1440, maxSize: 2 * 1024 * 1024 } // 2MB
} as const;

type ImageVariant = keyof typeof VARIANTS;
type ImageContext = 'map-pin' | 'list-card' | 'hero' | 'gallery' | 'thumbnail';

interface ResponsiveImageProps {
  displayId: string;
  imagePath: string;
  context: ImageContext;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
  fallbackSrc?: string;
}

// Context to variant mapping
const CONTEXT_TO_VARIANT: Record<ImageContext, ImageVariant> = {
  'map-pin': 'thumbnails/64x64',
  'thumbnail': 'thumbnails/150x150',
  'list-card': 'thumbnails/300x225',
  'hero': 'hero/800x600',
  'gallery': 'full/1920x1440'
};

// Device pixel ratio detection
const getDevicePixelRatio = (): number => {
  if (typeof window !== 'undefined') {
    return window.devicePixelRatio || 1;
  }
  return 1;
};

// Format support detection
const getSupportedFormats = (): string[] => {
  if (typeof window === 'undefined') return ['jpg'];
  
  const canvas = document.createElement('canvas');
  const formats = ['jpg'];
  
  if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
    formats.push('webp');
  }
  
  if (canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0) {
    formats.push('avif');
  }
  
  return formats;
};

// Generate optimized image URL
const generateImageUrl = (
  displayId: string,
  imagePath: string,
  variant: ImageVariant,
  format: string = 'jpeg'
): string => {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const bucket = 'display-images-organized';
  
  // Extract filename from path
  const filename = imagePath.split('/').pop() || 'image.jpg';
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
  
  // Generate new filename with format
  const newFilename = `${nameWithoutExt}.${format}`;
  
  return `${baseUrl}/storage/v1/object/public/${bucket}/${variant}/displays/${displayId}/${newFilename}`;
};

// Fallback URL generation (for original images)
const generateFallbackUrl = (imagePath: string): string => {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const bucket = 'display-assets';
  return `${baseUrl}/storage/v1/object/public/${bucket}/${imagePath}`;
};

export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  displayId,
  imagePath,
  context,
  alt,
  className,
  loading = 'lazy',
  onLoad,
  onError,
  fallbackSrc
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string>('');
  const [blurSrc, setBlurSrc] = useState<string>('');
  const [isInView, setIsInView] = useState(loading === 'eager');
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Get the appropriate variant for this context
  const variant = CONTEXT_TO_VARIANT[context];
  const variantConfig = VARIANTS[variant];

  // Set up intersection observer for lazy loading
  useEffect(() => {
    if (loading === 'lazy' && imgRef.current) {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [loading]);

  // Generate image URLs when in view
  useEffect(() => {
    if (!isInView) return;

    const formats = getSupportedFormats();
    const preferredFormat = formats.includes('avif') ? 'avif' : 
                           formats.includes('webp') ? 'webp' : 'jpeg';
    
    // Generate blur-up placeholder (smallest thumbnail)
    const blurUrl = generateImageUrl(displayId, imagePath, 'thumbnails/64x64', 'jpeg');
    setBlurSrc(blurUrl);
    
    // Try optimized variant first
    const optimizedUrl = generateImageUrl(displayId, imagePath, variant, preferredFormat);
    setCurrentSrc(optimizedUrl);
  }, [isInView, displayId, imagePath, variant]);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    
    // Try fallback formats
    const formats = getSupportedFormats();
    const currentFormat = currentSrc.split('.').pop();
    
    if (currentFormat === 'avif' && formats.includes('webp')) {
      setCurrentSrc(generateImageUrl(displayId, imagePath, variant, 'webp'));
      return;
    }
    
    if (currentFormat === 'webp' && formats.includes('jpeg')) {
      setCurrentSrc(generateImageUrl(displayId, imagePath, variant, 'jpeg'));
      return;
    }
    
    // Try fallback URL
    if (fallbackSrc) {
      setCurrentSrc(fallbackSrc);
    } else {
      setCurrentSrc(generateFallbackUrl(imagePath));
    }
    
    onError?.();
  };

  // Don't render until in view (for lazy loading)
  if (!isInView) {
    return (
      <div
        ref={imgRef}
        className={cn(
          "bg-muted animate-pulse",
          className
        )}
        style={{
          width: variantConfig.width,
          height: variantConfig.height,
          aspectRatio: `${variantConfig.width}/${variantConfig.height}`
        }}
      />
    );
  }

  return (
    <ImagePerformanceMonitor
      imageUrl={currentSrc}
      onMetrics={(metrics) => reportImagePerformance(metrics, context)}
    >
      <div className="relative overflow-hidden">
        {/* Blur-up placeholder */}
        {blurSrc && !isLoaded && (
          <img
            src={blurSrc}
            alt=""
            className={cn(
              "absolute inset-0 w-full h-full object-cover filter blur-sm scale-110",
              className
            )}
            style={{
              width: variantConfig.width,
              height: variantConfig.height,
              aspectRatio: `${variantConfig.width}/${variantConfig.height}`
            }}
          />
        )}
        
        {/* Loading placeholder */}
        {!blurSrc && !isLoaded && (
          <div
            className={cn(
              "absolute inset-0 bg-muted animate-pulse",
              className
            )}
            style={{
              width: variantConfig.width,
              height: variantConfig.height,
              aspectRatio: `${variantConfig.width}/${variantConfig.height}`
            }}
          />
        )}
        
        {/* Error placeholder */}
        {hasError && !currentSrc && (
          <div
            className={cn(
              "flex items-center justify-center bg-muted text-muted-foreground",
              className
            )}
            style={{
              width: variantConfig.width,
              height: variantConfig.height,
              aspectRatio: `${variantConfig.width}/${variantConfig.height}`
            }}
          >
            <span className="text-xs">Image unavailable</span>
          </div>
        )}
        
        {/* Actual image */}
        {currentSrc && (
          <img
            ref={imgRef}
            src={currentSrc}
            alt={alt}
            className={cn(
              "transition-all duration-500 ease-out",
              isLoaded ? "opacity-100 scale-100" : "opacity-0 scale-110",
              className
            )}
            loading={loading}
            onLoad={handleLoad}
            onError={handleError}
            style={{
              width: variantConfig.width,
              height: variantConfig.height,
              aspectRatio: `${variantConfig.width}/${variantConfig.height}`
            }}
          />
        )}
      </div>
    </ImagePerformanceMonitor>
  );
};

// Specialized components for different contexts
export const MapPinImage: React.FC<Omit<ResponsiveImageProps, 'context'>> = (props) => (
  <ResponsiveImage {...props} context="map-pin" />
);

export const DisplayCardImage: React.FC<Omit<ResponsiveImageProps, 'context'>> = (props) => (
  <ResponsiveImage {...props} context="list-card" />
);

export const HeroImage: React.FC<Omit<ResponsiveImageProps, 'context'>> = (props) => (
  <ResponsiveImage {...props} context="hero" />
);

export const GalleryImage: React.FC<Omit<ResponsiveImageProps, 'context'>> = (props) => (
  <ResponsiveImage {...props} context="gallery" />
);

export const ThumbnailImage: React.FC<Omit<ResponsiveImageProps, 'context'>> = (props) => (
  <ResponsiveImage {...props} context="thumbnail" />
);
